import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSalon } from '../store/slice/salonSlice';

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const defaultHours = DAYS.map((d) => ({
  day_of_week: d.value,
  open_time: '09:00',
  close_time: '18:00',
  is_closed: false,
  shift_label: null,
}));

function AddSalonPage() {
  const navigate = useNavigate();
  const { createSalon, loading, error } = useSalon();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    is_active: true,
  });
  const [hours, setHours] = useState(defaultHours);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleHoursChange = (dayOfWeek, field, value) => {
    setHours((prev) =>
      prev.map((h) =>
        h.day_of_week === dayOfWeek
          ? { ...h, [field]: field === 'is_closed' ? value : value }
          : h
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !formData.address_line_1?.trim() || !formData.city?.trim()) return;

    const payload = {
      ...formData,
      is_active: formData.is_active ? 1 : 0,
      hours: hours.map((h) => ({
        day_of_week: h.day_of_week,
        open_time: h.is_closed ? null : h.open_time,
        close_time: h.is_closed ? null : h.close_time,
        is_closed: h.is_closed,
        shift_label: h.shift_label || null,
      })),
    };

    if (imageFile) {
      const fd = new FormData();
      fd.append('name', payload.name);
      fd.append('description', payload.description || '');
      fd.append('address_line_1', payload.address_line_1);
      fd.append('address_line_2', payload.address_line_2 || '');
      fd.append('city', payload.city);
      fd.append('state', payload.state || '');
      fd.append('postal_code', payload.postal_code || '');
      fd.append('country', payload.country || 'USA');
      fd.append('phone', payload.phone || '');
      fd.append('email', payload.email || '');
      fd.append('latitude', payload.latitude || '');
      fd.append('longitude', payload.longitude || '');
      fd.append('is_active', payload.is_active);
      fd.append('hours', JSON.stringify(payload.hours));
      fd.append('image', imageFile);

      const result = await createSalon(fd);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Salon created successfully');
        navigate('/salons');
      }
    } else {
      const result = await createSalon(payload);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Salon created successfully');
        navigate('/salons');
      }
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <Link
          to="/salons"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Salons
        </Link>
      </div>

      <div className="card-premium overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h1 className="font-display font-bold text-2xl text-slate-800 tracking-tight">Add New Salon</h1>
          <p className="mt-1 text-slate-500">Create a new salon location</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
              Salon Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Luxe Beauty Salon"
              className="input-premium"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of the salon"
              rows="3"
              className="input-premium resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="address_line_1" className="block text-sm font-medium text-slate-700 mb-1.5">
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                id="address_line_1"
                name="address_line_1"
                type="text"
                value={formData.address_line_1}
                onChange={handleChange}
                placeholder="Street address"
                className="input-premium"
                required
              />
            </div>
            <div>
              <label htmlFor="address_line_2" className="block text-sm font-medium text-slate-700 mb-1.5">
                Address Line 2
              </label>
              <input
                id="address_line_2"
                name="address_line_2"
                type="text"
                value={formData.address_line_2}
                onChange={handleChange}
                placeholder="Suite, unit, etc."
                className="input-premium"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-slate-700 mb-1.5">
                City <span className="text-red-500">*</span>
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                className="input-premium"
                required
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-slate-700 mb-1.5">
                State
              </label>
              <input
                id="state"
                name="state"
                type="text"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
                className="input-premium"
              />
            </div>
            <div>
              <label htmlFor="postal_code" className="block text-sm font-medium text-slate-700 mb-1.5">
                Postal Code
              </label>
              <input
                id="postal_code"
                name="postal_code"
                type="text"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="ZIP / Postal code"
                className="input-premium"
              />
            </div>
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-1.5">
              Country
            </label>
            <input
              id="country"
              name="country"
              type="text"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country"
              className="input-premium"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
                className="input-premium"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@salon.com"
                className="input-premium"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-slate-700 mb-1.5">
                Latitude
              </label>
              <input
                id="latitude"
                name="latitude"
                type="text"
                value={formData.latitude}
                onChange={handleChange}
                placeholder="e.g., 40.7128"
                className="input-premium"
              />
            </div>
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-slate-700 mb-1.5">
                Longitude
              </label>
              <input
                id="longitude"
                name="longitude"
                type="text"
                value={formData.longitude}
                onChange={handleChange}
                placeholder="e.g., -74.0060"
                className="input-premium"
              />
            </div>
          </div>

          <div>
            <label htmlFor="image" className="block text-sm font-medium text-slate-700 mb-1.5">
              Cover Image
            </label>
            <input
              id="image"
              name="image"
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="input-premium"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
              Active (visible to customers)
            </label>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3">Business Hours</h3>
            <div className="space-y-2 border border-slate-200 rounded-premium divide-y divide-slate-100 overflow-hidden">
              {hours.map((h) => {
                const dayLabel = DAYS.find((d) => d.value === h.day_of_week)?.label || '';
                return (
                  <div key={h.day_of_week} className="p-4 flex flex-wrap items-center gap-3 bg-slate-50/50">
                    <span className="w-24 text-sm font-medium text-slate-700">{dayLabel}</span>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={h.is_closed}
                        onChange={(e) => handleHoursChange(h.day_of_week, 'is_closed', e.target.checked)}
                        className="rounded border-slate-300 text-amber-600"
                      />
                      <span className="text-xs text-slate-600">Closed</span>
                    </label>
                    {!h.is_closed && (
                      <>
                        <input
                          type="time"
                          value={h.open_time}
                          onChange={(e) => handleHoursChange(h.day_of_week, 'open_time', e.target.value)}
                          className="input-premium py-1.5 text-sm w-28"
                        />
                        <span className="text-slate-400">to</span>
                        <input
                          type="time"
                          value={h.close_time}
                          onChange={(e) => handleHoursChange(h.day_of_week, 'close_time', e.target.value)}
                          className="input-premium py-1.5 text-sm w-28"
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Creating...' : 'Create Salon'}
            </button>
            <Link
              to="/salons"
              className="px-6 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-premium hover:bg-slate-50 transition-all"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSalonPage;
